import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, BottomNavigation, BottomNavigationAction, Badge, useMediaQuery, useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import TagIcon from '@mui/icons-material/Tag';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import { AuthContext, MyContext } from '../context/AuthContext';
import { NotificationsContext } from '../context/NotificationsContext';
import { hapticTap } from '../lib/haptics';

/**
 * MOBILE TAB BAR
 *
 * A persistent bottom navigation for small screens. Replaces the
 * navbar drawer as the primary nav on mobile — gives the app a
 * native-feeling layout. Five tabs: Home, Feeds, Compose, Notifications,
 * Profile. Compose is the accented middle action (raised circle).
 *
 * Only renders on xs/sm. Hidden on desktop where the full sidebar nav
 * is available.
 */

interface MobileTabBarProps {
  /**
   * When true, skip the fixed positioning because the caller is placing
   * this component in a grid area (e.g. AppShell's `tabbar` slot). The
   * grid area already handles sticky bottom placement + safe-area insets,
   * so a second layer of fixed positioning would just create a phantom
   * duplicate. The internal media-query gate is also relaxed because the
   * CSS Module hides the tab bar on desktop.
   */
  inFlow?: boolean;
}

function MobileTabBar({ inFlow = false }: MobileTabBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const { notifications } = useContext(NotificationsContext);

  // Legacy (fixed) mode: gate on mobile + auth. Grid-slot mode trusts
  // AppShell CSS to hide the column above 1200px, but still needs auth.
  if (!userRecord) return null;
  if (!inFlow && !isMobile) return null;

  // Map current path to tab value.
  const value = (() => {
    const p = location.pathname;
    if (p === '/') return 'home';
    if (p.startsWith('/list/feeds') || p.startsWith('/feeds/')) return 'feeds';
    if (p === '/notifications') return 'notifs';
    if (p === `/${userRecord.username}`) return 'profile';
    return null;
  })();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleChange = (_: any, v: string) => {
    hapticTap();
    if (v === 'compose') {
      // Fire a global event that the existing FAB dialog listens for.
      // If not mounted, fall back to home focus.
      window.dispatchEvent(new CustomEvent('wipe-mobile-compose'));
      if (location.pathname !== '/') navigate('/');
      return;
    }
    switch (v) {
      case 'home': navigate('/'); break;
      case 'feeds': navigate('/list/feeds'); break;
      case 'notifs': navigate('/notifications'); break;
      case 'profile': navigate(`/${userRecord.username}`); break;
    }
  };

  const handleCompose = () => {
    hapticTap();
    window.dispatchEvent(new CustomEvent('wipe-mobile-compose'));
    if (location.pathname !== '/') navigate('/');
  };

  return (
    <>
      <Paper
        elevation={inFlow ? 0 : 8}
        sx={{
          // In-flow mode: the AppShell grid area is already sticky-bottom
          // with safe-area padding, so drop the fixed shell and render as
          // an ordinary block. Legacy mode keeps the fixed positioning for
          // backward compat with any code that still mounts this directly.
          ...(inFlow
            ? { width: '100%' }
            : {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1100,
                pb: 'env(safe-area-inset-bottom, 0px)',
              }),
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <BottomNavigation
          value={value ?? false}
          onChange={handleChange}
          showLabels={false}
          sx={{
            height: 58,
            bgcolor: 'background.paper',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              padding: '6px 0 8px',
              color: 'text.disabled',
              '&.Mui-selected': { color: 'primary.main' },
            },
          }}
        >
          <BottomNavigationAction value="home" icon={<HomeIcon />} aria-label="Home" />
          <BottomNavigationAction value="feeds" icon={<TagIcon />} aria-label="Feeds" />
          {/* Center spacer — the FAB sits visually here, but lives outside
              the BottomNavigation so it can pop above the bar without
              fighting AppShell's overflow-x clip. */}
          <BottomNavigationAction
            value="compose-spacer"
            disabled
            icon={<Box sx={{ width: 24, height: 24 }} />}
            sx={{ pointerEvents: 'none', opacity: 0 }}
          />
          <BottomNavigationAction
            value="notifs"
            aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
            icon={
              <Badge badgeContent={unreadCount} color="error" max={99} overlap="rectangular">
                <NotificationsIcon />
              </Badge>
            }
          />
          <BottomNavigationAction value="profile" icon={<PersonIcon />} aria-label="Profile" />
        </BottomNavigation>
      </Paper>

      {/* Floating compose FAB — fixed-position, sits above the tab bar.
          Rendered outside the Paper so AppShell's overflow-x:hidden on
          the .tabbar grid area can't clip it. */}
      <Box
        component="button"
        type="button"
        onClick={handleCompose}
        aria-label="Compose post"
        sx={{
          position: 'fixed',
          bottom: 'calc(58px + env(safe-area-inset-bottom, 0px) - 24px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: '#fff',
          border: '2px solid',
          borderColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          zIndex: 1101,
          p: 0,
          transition: 'transform 0.1s ease-out',
          '&:active': { transform: 'translateX(-50%) scale(0.92)' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        <AddIcon sx={{ color: '#fff', fontSize: 28 }} />
      </Box>
    </>
  );
}

export default MobileTabBar;
